import { useEffect } from 'react';
import { C } from '../constants';
import { setMeta } from '../utils/meta';
import Glass from '../components/Glass';

const accent = C.cyan;

const STATES_TABLE = [
  { state: 'Uttar Pradesh',    seats: 403, districts: 75,  timing: 'Feb–Mar 2027', context: 'India\'s largest state' },
  { state: 'Punjab',           seats: 117, districts: 23,  timing: 'Feb–Mar 2027', context: 'AAP re-election test' },
  { state: 'Goa',              seats: 40,  districts: 2,   timing: 'Feb–Mar 2027', context: 'Multi-party coastal state' },
  { state: 'Uttarakhand',      seats: 70,  districts: 13,  timing: 'Feb–Mar 2027', context: 'Alternation tradition' },
  { state: 'Manipur',          seats: 60,  districts: 16,  timing: 'Feb–Mar 2027', context: 'Complex ethnic dynamics' },
  { state: 'Himachal Pradesh', seats: 68,  districts: 12,  timing: 'Nov 2027',     context: 'Strong alternation pattern' },
  { state: 'Gujarat',          seats: 182, districts: 33,  timing: 'Dec 2027',     context: 'Governed by same party since 1998' },
];

const STATE_PROFILES = [
  {
    state: 'Uttar Pradesh',
    badge: '75 Districts · 403 Seats',
    timing: 'Feb–Mar 2027',
    q: 'Why Is the Uttar Pradesh 2027 Election So Important?',
    body: 'With 403 seats and 75 districts, UP is India\'s largest state and its result shapes the entire Lok Sabha narrative heading into 2029. Uttar Pradesh sends the most members to the Lok Sabha — the state\'s assembly election is essentially a national election in miniature. Whoever controls UP has a significant structural advantage in any general election. The 2027 contest features a multi-party field with distinct regional and demographic bases. UnofficialElections will cover all 75 districts with real-time results — a scale of district-level coverage that will capture patterns statewide polls typically miss: regional variations, urban-rural divides, and local dynamics.',
  },
  {
    state: 'Punjab',
    badge: '23 Districts · 117 Seats',
    timing: 'Feb–Mar 2027',
    q: 'What Is at Stake in Punjab\'s 2027 Election?',
    body: 'Punjab 2027 is AAP\'s first re-election test after forming government in 2022, with Congress and other parties challenging for the state\'s 117 seats. The party swept to power on an anti-establishment wave, but governing brings a different set of challenges than campaigning. Congress, the traditional alternating party in Punjab, and other parties will all aim to challenge. The platform will cover all 23 districts, capturing how urban centres like Ludhiana and Amritsar compare with rural Punjab.',
  },
  {
    state: 'Goa',
    badge: '2 Districts · 40 Seats',
    timing: 'Feb–Mar 2027',
    q: 'What Makes Goa\'s 2027 Election Politically Significant?',
    body: 'Goa is small (40 seats, 2 districts) but politically dramatic, with multi-party contests and frequent post-election realignments making every seat count. The state has a history of elected members switching parties after elections. The 2027 contest will feature multiple parties competing for a small number of seats, making it one of the most unpredictable elections of the year.',
  },
  {
    state: 'Uttarakhand',
    badge: '13 Districts · 70 Seats',
    timing: 'Feb–Mar 2027',
    q: 'Will Uttarakhand Continue Its Anti-Incumbency Pattern in 2027?',
    body: 'Uttarakhand has alternated between parties at every assembly election since its formation in 2000 — one of the strongest alternation patterns in Indian politics. Whether it holds in 2027 or breaks will be a closely watched question. The platform will track all 13 districts, including Dehradun and Haridwar, capturing urban-rural and hill-plain dynamics.',
  },
  {
    state: 'Manipur',
    badge: '16 Districts · 60 Seats',
    timing: 'Feb–Mar 2027',
    q: 'What Makes Manipur\'s 2027 Election Politically Complex?',
    body: 'Manipur\'s 60-seat assembly election is shaped by ethnic dynamics, security concerns, and a political environment that does not follow national patterns. The 2027 election will be shaped by local concerns that do not map neatly onto national political narratives. The state\'s 16 districts reflect this diversity, and the platform will cover each one.',
  },
  {
    state: 'Himachal Pradesh & Gujarat',
    badge: '12 + 33 Districts',
    timing: 'Nov–Dec 2027',
    q: 'What Should We Expect from Himachal Pradesh and Gujarat in Late 2027?',
    body: 'Himachal Pradesh (November 2027) has one of India\'s most reliable alternation patterns, switching between parties at nearly every election. The 2027 contest will test whether this continues. Gujarat (December 2027) has been governed by the same party without interruption since 1998. The question in Gujarat is less about who wins and more about the margin and whether any opposition party can establish a meaningful presence. The platform will cover all 33 Gujarat districts.',
  },
];

export default function SevenStates2027Screen() {
  useEffect(() => {
    setMeta({
      title:       "Seven States 2027 - India's Next Big Election Year Guide | UnofficialElections",
      description: "Complete guide to India's seven state elections in 2027: UP, Punjab, Gujarat, Goa, Uttarakhand, HP, and Manipur. District-level data on UnofficialElections.",
      ogTitle:     "Seven States 2027 - India's Next Big Election Year",
      ogImage:     '/og-home.png',
      canonical:   '/2027',
    });
  }, []);
  return (
    <main className="px-5 md:px-10 py-10 max-w-4xl mx-auto">
      <div style={{ fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>
        2027 Elections
      </div>
      <h1 style={{
        fontWeight: 700, fontSize: 'clamp(1.8rem, 4.5vw, 3rem)',
        lineHeight: 1.05, letterSpacing: '-0.025em', marginTop: '0.4rem', color: C.white,
      }}>
        Seven States in 2027
      </h1>
      <p style={{ fontSize: '0.95rem', color: C.textDim, marginTop: '0.6rem', maxWidth: 640, lineHeight: 1.65 }}>
        2027 is India's most consequential election year before the 2029 Lok Sabha. Seven state assemblies go to the polls — from Uttar Pradesh in the north to Goa on the western coast. UnofficialElections will cover each state with full district-level maps, all contesting parties, and real-time results.
      </p>

      {/* Overview table */}
      <div style={{ marginTop: '2rem' }}>
        <Glass style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: `1px solid ${C.border}` }}>
                  {['State', 'Seats', 'Districts', 'Expected', 'Key Context'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: C.textDim, fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {STATES_TABLE.map((row, i) => (
                  <tr key={i} style={{ borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
                    <td style={{ padding: '10px 14px', color: C.white, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.state}</td>
                    <td style={{ padding: '10px 14px', color: C.textDim }}>{row.seats}</td>
                    <td style={{ padding: '10px 14px', color: C.textDim }}>{row.districts}</td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ color: accent, fontWeight: 600, fontSize: '0.78rem', background: 'rgba(0,229,255,0.08)', padding: '2px 8px', borderRadius: 999 }}>
                        {row.timing}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem' }}>{row.context}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Glass>
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '3rem', marginBottom: '2.5rem' }} />

      {/* State profiles */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
        {STATE_PROFILES.map((profile, i) => (
          <div key={i}>
            <div className="flex items-center gap-3 flex-wrap" style={{ marginBottom: '0.5rem' }}>
              <h2 style={{ fontWeight: 700, fontSize: '1.1rem', color: C.white, letterSpacing: '-0.01em', margin: 0 }}>
                {profile.q}
              </h2>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: '0.6rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', color: accent, fontWeight: 600, background: 'rgba(0,229,255,0.08)', padding: '2px 10px', borderRadius: 999 }}>
                {profile.badge}
              </span>
              <span style={{ fontSize: '0.75rem', color: C.textDim, fontWeight: 600, background: 'rgba(255,255,255,0.04)', padding: '2px 10px', borderRadius: 999 }}>
                {profile.timing}
              </span>
            </div>
            <p style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.75, margin: 0 }}>
              {profile.body}
            </p>
          </div>
        ))}
      </div>

      {/* National stakes */}
      <div style={{ marginTop: '3rem' }}>
        <h2 style={{ fontWeight: 700, fontSize: '1.1rem', color: C.white, marginBottom: '0.5rem' }}>
          What Is at Stake Nationally?
        </h2>
        <p style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.75, margin: 0 }}>
          The seven states voting in 2027 collectively account for a significant share of India's Lok Sabha seats. Their outcomes will determine which parties enter the 2029 general election cycle with momentum and which enter it on the defensive. Each state also offers a district-level window into the regional dynamics that national coverage typically misses.
        </p>
      </div>

      {/* Methodology */}
      <div style={{ marginTop: '2.5rem' }}>
        <h2 style={{ fontWeight: 700, fontSize: '1.1rem', color: C.white, marginBottom: '0.5rem' }}>
          How Does the Platform Handle Accuracy for Multi-State Coverage?
        </h2>
        <p style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.75, margin: 0 }}>
          UnofficialElections applies the same methodology to every state. Each device is limited to one vote per state. No personal data is collected. All results are displayed transparently with no hidden weighting. The platform does not claim to predict election outcomes — it is a sentiment poll that becomes more valuable as more people participate. The known limitation is the sample's skew toward urban, internet-connected voters. Each state's data should be read with this bias in mind.
        </p>
      </div>

      <Glass style={{ marginTop: '2.5rem', padding: '1.1rem 1.3rem', border: `1px solid ${accent}22` }}>
        <p style={{ fontSize: '0.88rem', color: C.textDim, margin: 0, lineHeight: 1.65 }}>
          Follow each state as it goes live on the platform. District-level data reveals patterns that statewide coverage misses. The more people who participate from each state, the richer the data becomes. Follow <strong style={{ color: C.white }}>@unofficialelections</strong> on Instagram for announcements as each state goes live.
        </p>
      </Glass>

      <Glass style={{ marginTop: '1.5rem', padding: '1rem 1.3rem' }}>
        <p style={{ fontSize: '0.85rem', color: C.textDim, margin: 0, lineHeight: 1.6 }}>
          UnofficialElections.com — India's elections, your voice, every district. Unofficial, non-political, entertainment only.
        </p>
      </Glass>
    </main>
  );
}
